import { useEffect } from "react";
import { useCommunication } from "./CommunicationWrapper";
import Color from "color";
import { defaultDarkPreset } from "./constants";
import { StorageArea } from "./communication";


// Helper function to transform font family name to Google Fonts format
function transformFontFamily(fontFamily: string): string {
    return (fontFamily || "").replace(/\s+/g, '+');
}

export function loadCustomFont(options: any) {
    let link = document.getElementById("bettercanvas_custom_font_link");
    let style = document.getElementById("bettercanvas_custom_font");



    let font = options?.theme_enabled ? (options?.custom_font || "Lato") : "Lato";


    let load = () => {

        document.head.appendChild(style as Node);
        const transformedFamily = transformFontFamily(font);
        (link as HTMLLinkElement).href = `https://fonts.googleapis.com/css2?family=${transformedFamily}:wght@400;500;600;700&display=swap`;
        (link as HTMLLinkElement).rel = "stylesheet";
        document.head.appendChild(link as Node);

        (style as HTMLStyleElement).textContent = `*, input, a, button, h1, h2, h3, h4, h5, h6, p, span {font-family: '${font}'!important}:root {--bcfont: '${font}'!important}`;
    }

    let createEls = () => {
        link = document.createElement("link");
        link.id = "bettercanvas_custom_font_link";
        style = document.createElement("style");
        style.id = "bettercanvas_custom_font";
        load();
    }

    if (link && style) {
        load();
    } else if (font) {
        if (document.readyState !== 'loading') {
            createEls();
        } else {
            document.addEventListener("DOMContentLoaded", () => {
                createEls();
            });
        }
    }
}

const extractGradientColors = (value: string): string[] => {
    const match = value.match(/linear-gradient\((.*?)\)/);
    if (!match) return ["#000000", "#000000"];
    const gradientContent = match[1];
    const colors = gradientContent
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.startsWith("#") || part.startsWith("rgb") || part.startsWith("hsl"))
        .map((color) => {
            try {
                return new Color(color).hex();
            } catch (e) {
                return "#000000";
            }
        });
    return colors;
};

const isUsingDarkMode = (options: any) => {

    if (options?.device_dark === true) {
        const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");
        const isDarkMode = prefersDarkScheme.matches;
        return isDarkMode ? true : false;
    }

    if (options?.auto_dark === true) {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();

        const startTime = parseInt(options?.auto_dark_start?.hour) * 60 + parseInt(options?.auto_dark_start?.minute);
        const endTime = parseInt(options?.auto_dark_end?.hour) * 60 + parseInt(options?.auto_dark_end?.minute);

        // Handle cases where end time is next day (e.g., 8 PM to 8 AM)
        if (startTime > endTime) {
            // Dark period spans midnight
            return currentTime >= startTime || currentTime < endTime;
        } else {
            // Dark period is within same day
            return currentTime >= startTime && currentTime < endTime;
        }
    }

    return options?.dark_mode ? true : false;
};

export function generateDarkModeVars(options: StorageArea) {

    options = options || {};
    let preset;
    const darkMode = isUsingDarkMode(options);
    if (options.theme_enabled === true) {
        const mode = darkMode ? "dark_preset" : "light_preset";
        preset = options?.[mode] || {};
    } else {
        preset = darkMode ? defaultDarkPreset : {};
    }

    let css = `
        body {
            transition: background-color 0.3s ease !important;
    `;

    if (!preset?.["cards"]) {
        preset["cards"] = "#00000000";
    }

    Object.keys(preset).forEach((key) => {
        if (typeof preset[key] !== 'string') return
        if (preset[key].includes("linear-gradient")) {
            const extracted = extractGradientColors(preset[key]);
            css += "--bc" + key + "-ungradient:" + extracted[0] + ";";
        }

        css += "--bc" + key + ":" + preset[key] + ";";
    });
    css += `--bcshadow: 0 0 10px 0 ${options?.dark_preset?.["borders"]}40;`;


    return css + "}";
}

export const WithTheme = () => {

    const { useRerenderListener } = useCommunication();

    const { options, trigger } = useRerenderListener(["dark_mode", "theme_enabled", "dark_preset", "light_preset"]);

    useEffect(() => {
        loadCustomFont(options);
    }, [trigger]);

    return (
        <style>
            {generateDarkModeVars(options)}
            {themeStyles}
        </style>
    )
}

const themeStyles = `
.bg-theme-0 {
background:  var(--bcbackground-0, #fff)
}
.bg-theme-1 {
background: var(--bcbackground-1, #f2f4f4);
}
.bg-theme-2 {
background: var(--bcbackground-2, #e4e8e8);
}
.bg-sidebar {
background: var(--bcsidebar, #e4e8e8);
}
.\!bg-theme-0 {
background: var(--bcbackground-0, #fff) !important;
}
.\!bg-theme-1 {
background: var(--bcbackground-1, #f2f4f4) !important;
}
.\!bg-theme-2 {
background: var(--bcbackground-2, #e4e8e8) !important;
}
.text-theme-0 {
color: var(--bctext-0, #273540);
}
.text-theme-1 {
color: var(--bctext-1, #777777);
}
.text-theme-2 {
color: var(--bctext-2, #ababab);
}
.\!text-theme-0 {
color: var(--bctext-0, #273540) !important;
}
.\!text-theme-1 {
color: var(--bctext-1, #777777) !important;
}
.text-theme-link {
color: var(--bclinks, #4a90e2);
}
.text-sidebar {
color: var(--bcsidebar-text, #273540);
}
.text-sidebar-default {
color: var(--bcsidebar-text, #fff);
}
.border-theme {
border-color: var(--bcborders, #e8eaec);
}
.border-theme-link {
border-color: var(--bclinks, #4a90e2);
}
.ring-theme {
ring-color: var(--bctext-0, #273540) !important;
}
.hover\:bg-theme-0:hover {
background: var(--bcbackground-0, #fff) !important;
}
.hover\:bg-theme-1:hover {
background: var(--bcbackground-1, #f2f4f4) !important;
}
.hover\:bg-theme-2:hover {
background: var(--bcbackground-2, #e4e8e8) !important;
}
.hover\:border-theme:hover {
border-color: var(--bcborders, #e8eaec) !important;
}
.fill-theme-links {
fill: var(--bclinks, #4a90e2);
}
.fill-text-theme-0 {
fill: var(--bctext-0, #273540);
}
.fill-bg-theme-2 {
fill: var(--bcbackground-2, #e4e8e8) !important;
}
.switch-checked[data-state="checked"] {
background: var(--bclinks, #4a90e2);
}
.switch-unchecked[data-state="unchecked"] {
background: var(--bcbackground-2, #e4e8e8);
}
.select-open-theme[data-state="open"] {
border-color: var(--bcborders, #e8eaec) !important;
}
.shadow-theme {
box-shadow: var(--bcshadow, 0 0 10px 0 #e8eaec50);
}
.font-theme {
font-family: var(--bcfont, "Lato Extended"), sans-serif;
}
.bg-brand-gradient {
background: linear-gradient(90deg, #ffa952, #ff686c);
}
.bg-brand-gradient-alt {
background: linear-gradient(90deg, #ef54c3, #ac92ff);
}

.data-\[state\=on\]\:bg-theme-2[data-state="on"] {
background: var(--bcbackground-2, #e4e8e8) !important;
}
.data-\[state\=on\]\:text-theme-0[data-state="on"] {
color: var(--bctext-0, #273540) !important;
}

/* inverses */

.data-\[selected-single\=true\]\:bg-theme-text-0[data-selected-single="true"],
.bg-theme-text-0 {
background: var(--bctext-0, #273540) !important;
}
.data-\[selected-single\=true\]\:text-theme-bg-0[data-selected-single="true"],
.text-theme-bg-0 {
color: var(--bcbackground-0, #fff) !important;
}

.bg-theme-link {
background: var(--bclinks, #4a90e2);
}
.bg-theme-text-0 {
background: var(--bctext-0, #273540);
}
.bg-theme-text-sidebar {
background: var(--bcsidebar-text, #273540);
}
.text-theme-bg {
color: var(--bcbackground-0, #fff);
}
.hover\:text-theme-bg:hover {
color: var(--bcbackground-0, #fff);
}
.hover\:text-sidebar:hover {
color: var(--bcsidebar-text, #273540);
}
.text-theme-bg-ungradient {
color: var(--bcbackground-0-ungradient, #fff);
}
.bg-theme-border {
background: var(--bcborders, #e8eaec);
}
.bg-theme-0-ungradient {
background: var(--bcbackground-0-ungradient, #fff);
}
.bg-sidebar-ungradient {
background: var(--bcsidebar-ungradient, #e4e8e8);
}
` 